import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import { formatDateTimeFromApi } from '../../utils/dateTime';
import {
  getNotificationChannelLabel,
  getNotificationChannelSetting,
  updateNotificationChannelSetting,
  type NotificationChannelSetting,
} from '../../utils/notificationChannelSetting';
import { ArrowLeft, Bell, History, RefreshCw } from 'lucide-react';

interface FormErrors {
  senderKey?: string;
}

const FormRow: React.FC<{
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
  error?: string;
}> = ({ label, required, hint, children, error }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 border-b border-gray-100 last:border-0">
    <div className="w-52 sm:w-56 flex-shrink-0">
      <p className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </p>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
    <div className="flex-1 min-w-0">
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  </div>
);

const NotificationChannelUpdate: React.FC = () => {
  const navigate = useNavigate();
  const { projectCode: projectCodeFromUrl, channel: channelFromUrl } = useParams<{
    projectCode: string;
    channel: string;
  }>();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('notification-channel-list');

  const [formData, setFormData] = useState({
    senderKey: '',
  });
  const [initialData, setInitialData] = useState<NotificationChannelSetting | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  const listPath = `/projects/${projectCodeFromUrl}/notification-channel-settings`;

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    if (!projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
  }, [projectCodeFromUrl, navigate]);

  useEffect(() => {
    if (!projectCodeFromUrl || !channelFromUrl || !projectDetail) return;
    let cancelled = false;
    setSubmitError(null);
    setIsLoading(true);
    getNotificationChannelSetting(projectCodeFromUrl, channelFromUrl)
      .then((setting) => {
        if (cancelled || !setting) return;
        setInitialData(setting);
        setFormData({
          senderKey: setting.senderKey || '',
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setSubmitError(err instanceof Error ? err.message : '알림 채널 정보를 불러오지 못했습니다.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectCodeFromUrl, channelFromUrl, projectDetail]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!formData.senderKey.trim()) next.senderKey = '발송키는 필수입니다.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate() || !projectCodeFromUrl || !channelFromUrl) return;
    try {
      setIsSubmitting(true);
      await updateNotificationChannelSetting(projectCodeFromUrl, channelFromUrl, {
        senderKey: formData.senderKey.trim(),
      });
      navigate(listPath, { replace: true });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '알림 채널 수정에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    if (initialData) {
      setFormData({
        senderKey: initialData.senderKey || '',
      });
    }
    setErrors({});
    setSubmitError(null);
  };

  if (!projectDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-500">프로젝트 정보를 불러올 수 없습니다.</p>
          <button
            type="button"
            onClick={() => navigate('/projects')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            프로젝트 목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="알림 채널 수정"
          subtitle={
            projectCodeFromUrl && channelFromUrl
              ? `${projectDetail.name} / ${getNotificationChannelLabel(channelFromUrl)}`
              : '알림 채널 정보를 수정합니다'
          }
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <button
                type="button"
                onClick={() => navigate(listPath)}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                알림 채널 목록으로
              </button>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : submitError && !initialData ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <p className="text-red-600 mb-4">{submitError}</p>
                <button
                  type="button"
                  onClick={() => navigate(listPath)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  목록으로
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {initialData && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">
                          {getNotificationChannelLabel(initialData.channel)} ({initialData.channel})
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                          <div className="flex items-center">
                            <History className="w-4 h-4 mr-2 flex-shrink-0" />
                            생성: {formatDateTimeFromApi(initialData.createdAt) || '—'} ({initialData.createdBy ?? '—'})
                          </div>
                          <div className="flex items-center">
                            <RefreshCw className="w-4 h-4 mr-2 flex-shrink-0" />
                            수정: {formatDateTimeFromApi(initialData.updatedAt) || '—'} ({initialData.updatedBy ?? '—'})
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {submitError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {submitError}
                  </div>
                )}

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Bell className="w-5 h-5" />
                      기본 정보
                    </h2>
                  </div>
                  <div className="px-4 lg:px-6 py-2 space-y-0">
                    <FormRow label="채널">
                      <input
                        type="text"
                        value={
                          channelFromUrl
                            ? `${getNotificationChannelLabel(channelFromUrl)} (${channelFromUrl})`
                            : ''
                        }
                        readOnly
                        className="block w-full min-w-[200px] px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600"
                      />
                    </FormRow>
                    <FormRow
                      label="발송키"
                      required
                      hint="알림톡은 BizGo senderKey를 입력하세요."
                      error={errors.senderKey}
                    >
                      <input
                        type="text"
                        value={formData.senderKey}
                        onChange={(e) => {
                          setFormData((p) => ({ ...p, senderKey: e.target.value }));
                          setErrors((e2) => ({ ...e2, senderKey: undefined }));
                        }}
                        placeholder="발송키를 입력하세요"
                        className={`block w-full min-w-[200px] px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.senderKey ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                    </FormRow>
                  </div>
                </div>

                <div className="flex justify-between items-center mt-6">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center text-sm font-medium text-gray-700"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    초기화
                  </button>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => navigate(listPath)}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm font-medium"
                    >
                      {isSubmitting ? '수정 중...' : '알림 채널 수정'}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default NotificationChannelUpdate;
